import { Add, Cached } from '@mui/icons-material';
import { Alert, Button, Divider, Grid, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FieldArray, useFormikContext } from 'formik';
import { Edit2, Trash, Warning2 } from 'iconsax-react';
import { type ReactElement, useContext, useEffect } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  EmptyFormikQuote,
  type QuoteFormikType,
  type QuoteSubmitType,
  type RequiredQuoteFieldsInfo,
} from '@core/formik/quote';
import { type ContactData, useContactCreation } from '@core/hooks/useContactCreation';
import Enums from '@core/types/api/Enums';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { ContactField } from '@web/components/Contact/ContactField';
import { FieldWithCustomChip } from '@web/components/FieldWithCustomChip/FieldWithCustomChip';
import { Input } from '@web/components/Input/Input';
import { ApiLookupInput } from '@web/components/LookupInput/ApiLookupInput';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import type { QueryRouteBarrelTypes } from '@web/utils/ApiClient';
import { isValidReference, lookups } from '@web/utils/DomainHelpers';

import {
  getPricingChipLabel,
  getValidCompanyId,
  isQuoteContact,
} from './buyerInformationSectionHelpers';
import { InlineCompanyCreate } from './InlineCompanyCreate';
import type { ProductRowsOverride } from './useBuyerPricingReconciler';

export const BuyerInformationSection = ({
  submitType,
  requiredFields,
  disableCreate = false,
  reconcileBuyerPricing,
}: {
  submitType: QuoteSubmitType;
  requiredFields: RequiredQuoteFieldsInfo;
  disableCreate?: boolean;
  reconcileBuyerPricing: (
    newCompanyID: string | null,
    productsOverride?: ProductRowsOverride,
  ) => Promise<void> | void;
}): ReactElement => {
  const ctx = useContext(SlabContext);
  const theme = useTheme();
  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);
  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);

  const formikBag = useFormikContext<QuoteFormikType>();

  const hasTierPricing =
    ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    ctx.userInfo.tenant.tierPricing;
  const companyId = formikBag.values.company?.id ?? null;
  const { data: fixedPricingByCompany } = useSlabQuery(
    'GET tier pricing by company ID',
    { queryParams: { companyIds: [companyId ?? ''] } },
    { enabled: hasTierPricing && hasStringValue(companyId) },
  );
  const allProductsByPlants = [
    ...formikBag.values.mainProductsByPlants,
    ...formikBag.values.additionalProductsByPlants,
  ];
  const pricingChipLabel = getPricingChipLabel(
    hasTierPricing,
    fixedPricingByCompany,
    allProductsByPlants,
  );

  const { data: projectData } = useSlabQuery(
    'GET project by ID',
    { pathParams: { id: formikBag.values.project.id ?? '' } },
    { enabled: formikBag.values.project.id !== null && submitType === 'saveAsNewQuote' },
  );

  useEffect(() => {
    if (submitType === 'saveAsNewQuote' && hasValue(projectData?.projectJobNumber)) {
      formikBag.setFieldValue('projectJobNumber', projectData.projectJobNumber);
    }
  }, [submitType, projectData]);

  // Fetch project companies data to find associated contacts
  const { data: projectCompanies } = useSlabQuery(
    'GET project companies by project ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: formikBag.values.project.id !== null,
    },
  );

  const companyDisabled = !isValidReference(formikBag.values.project);
  const companyLabel = companyDisabled ? 'Select a project' : 'Company';

  const contactDisabled = !isValidReference(formikBag.values.company);
  const contactLabel = ((): string => {
    if (companyDisabled) {
      return 'Select a project';
    }
    if (contactDisabled) {
      return 'Select a company';
    }
    return 'Contact';
  })();

  const selectedCompany = projectCompanies?.find(
    (pc) => pc.company.id === formikBag.values.company?.id,
  );

  const hasDispatchCustomers =
    hasValue(selectedCompany?.company.dispatchCustomers) &&
    selectedCompany?.company.dispatchCustomers.length > 0;

  const hasInactiveDispatchCustomer =
    hasValue(selectedCompany?.company.dispatchCustomers) &&
    selectedCompany.company.dispatchCustomers.some((dc) => dc.inactive);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {hasValue(selectedCompany) && !hasDispatchCustomers && (
        <Alert severity='warning' icon={<Warning2 variant='TwoTone' />}>
          <Typography variant='body1' fontWeight={500}>
            Link to dispatch needed
          </Typography>
          <Typography variant='body1'>
            This prospect needs a dispatch link before quotes can be exported.
          </Typography>
        </Alert>
      )}
      {hasValue(selectedCompany) && hasInactiveDispatchCustomer && (
        <Alert severity='warning' icon={<Warning2 variant='TwoTone' />}>
          <Typography variant='body1' fontWeight={500}>
            Dispatch customer inactive
          </Typography>
          <Typography variant='body1'>
            This company has an inactive dispatch customer. Quotes will not export to that dispatch.
          </Typography>
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FieldWithCustomChip
          label={pricingChipLabel ?? ''}
          showChip={pricingChipLabel !== null}
          chipBorderColor={theme.palette.SlabGreen['600']}
          chipTextColor={theme.palette.SlabGreen['600']}
          extraChips={
            hasValue(selectedCompany) ? [{ label: selectedCompany.company.category }] : []
          }
          field={
            <InlineCompanyCreate
              disabled={companyDisabled}
              disableCreate={disableCreate}
              onCompanyCreated={(company, contact) => {
                SetFormikValue(formikBag, 'company', company);
                SetFormikValue(
                  formikBag,
                  'contacts',
                  hasValue(contact) ? [contact] : EmptyFormikQuote.contacts,
                );
                reconcileBuyerPricing(company.id);
              }}
            >
              <ApiLookupInput
                formState={formikBag.values}
                name='company'
                label={requiredLabel(companyLabel, requiredFields.company)}
                disabled={companyDisabled}
                route={{
                  barrel: 'GET project companies by project ID',
                  args: () => ({
                    pathParams: {
                      id: formikBag.values.project.id ?? '',
                    },
                  }),
                  transform: (pcs): LookupInputOption[] => {
                    return lookups({
                      items: pcs.map((pc) => pc.company),
                      label: (pc) => pc.name,
                      sublabels: (pc) => pc.lookupSublabels(usesDispatchCustomer),
                      count: pcs.length,
                    });
                  },
                  options: {
                    enabled: formikBag.values.project.id !== null,
                  },
                }}
                onMatchChange={(newCompanyID): void => {
                  if (newCompanyID === null) {
                    SetFormikValue(formikBag, 'contacts', EmptyFormikQuote.contacts);
                  } else {
                    const selectedProjectCompany = projectCompanies?.find(
                      (pc) => pc.company.id === newCompanyID,
                    );

                    if (hasValue(selectedProjectCompany?.contact)) {
                      SetFormikValue(formikBag, 'contacts', [
                        {
                          id: selectedProjectCompany.contact.id,
                          option: {
                            value: selectedProjectCompany.contact.id,
                            label: selectedProjectCompany.contact.fullName(),
                          },
                          isNewContact: false,
                        },
                      ]);
                    } else {
                      SetFormikValue(formikBag, 'contacts', EmptyFormikQuote.contacts);
                    }
                  }
                  reconcileBuyerPricing(newCompanyID);
                }}
              />
            </InlineCompanyCreate>
          }
        />
      </Box>

      <FieldArray
        name='contacts'
        render={(arrayHelpers): ReactElement => {
          const { createNewContact } = useContactCreation({
            company:
              hasValue(formikBag.values.company?.id) && formikBag.values.company?.id !== ''
                ? {
                    id: formikBag.values.company.id,
                    option: formikBag.values.company.option,
                  }
                : null,
            onCreateNew: (contact: ContactData) => {
              arrayHelpers.push(contact);
            },
            onCancelNew: () => {
              // This will be called by individual contact rows
            },
          });

          return (
            <>
              {formikBag.values.contacts.map((contact, index) => {
                if (!isQuoteContact(contact)) {
                  return null;
                }
                return (
                  <Box
                    key={
                      contact.isNewContact
                        ? `new-contact-${index}`
                        : (contact.id ?? `contact-${index}`)
                    }
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
                  >
                    {contact.isNewContact ? (
                      <>
                        <ContactField
                          requiresContactInfo={requiresContactInfo}
                          requiresContactEmail={requiresContactEmail}
                          name={`contacts.${index}`}
                        />
                        <IconButton
                          sx={{ mt: 1, color: theme.palette.SlabIndigo['700'] }}
                          onClick={() => {
                            const updatedContact = {
                              id: null,
                              option: null,
                              isNewContact: false,
                            };
                            SetFormikValue(formikBag, `contacts.${index}`, updatedContact);
                          }}
                        >
                          <Cached />
                        </IconButton>
                        <IconButton sx={{ mt: 1 }} onClick={() => arrayHelpers.remove(index)}>
                          <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Box sx={{ flex: 1 }}>
                          <ApiLookupInput
                            formState={formikBag.values}
                            name={`contacts.${index}`}
                            label={contactLabel}
                            disabled={contactDisabled}
                            route={{
                              barrel: 'GET contacts',
                              args: (
                                inputText,
                                { constructQueryParams },
                              ): QueryRouteBarrelTypes['GET contacts']['args'] =>
                                constructQueryParams(inputText, {
                                  filterBy: [
                                    {
                                      name: 'companyId',
                                      operation: Enums.FilterOperation.Equals,
                                      values: [getValidCompanyId(formikBag.values.company)],
                                    },
                                  ],
                                }),
                              transform: (contactList): LookupInputOption[] => {
                                const selectedIds = new Set(
                                  formikBag.values.contacts.flatMap((c, i) =>
                                    i !== index && isQuoteContact(c) ? [c.id] : [],
                                  ),
                                );
                                const filteredItems = (contactList.items ?? []).filter(
                                  (c) => !selectedIds.has(c.id),
                                );
                                const filteredCount = Math.max(
                                  0,
                                  (contactList.count ?? 0) - selectedIds.size,
                                );

                                return lookups({
                                  items: filteredItems,
                                  count: filteredCount,
                                  label: (c) => c.fullName(),
                                });
                              },
                              options: {
                                enabled: formikBag.values.company?.id !== null,
                              },
                            }}
                          />
                        </Box>
                        <IconButton
                          sx={{ mt: 1 }}
                          onClick={() => {
                            const updatedContact: ContactData = {
                              id: null,
                              option: null,
                              isNewContact: true,
                              contact: {
                                firstName: '',
                                lastName: '',
                                emailAddress: '',
                                phoneNumber: '',
                                jobTitle: '',
                                notes: null,
                                company: formikBag.values.company,
                                fullName: () => '',
                              },
                            };
                            SetFormikValue(formikBag, `contacts.${index}`, updatedContact);
                          }}
                        >
                          <Edit2 variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                        </IconButton>
                        <IconButton sx={{ mt: 1 }} onClick={() => arrayHelpers.remove(index)}>
                          <Trash variant='TwoTone' color={theme.palette.SlabIndigo['700']} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  disabled={contactDisabled}
                  onClick={(): void => {
                    const newContact: ContactData = {
                      id: null,
                      option: null,
                      isNewContact: false,
                    };
                    arrayHelpers.push(newContact);
                  }}
                  sx={{ textTransform: 'none' }}
                  startIcon={<Add />}
                >
                  Add existing contact
                </Button>
                <Button
                  disabled={contactDisabled}
                  onClick={createNewContact}
                  sx={{ textTransform: 'none' }}
                  startIcon={<Edit2 />}
                >
                  Create new contact
                </Button>
              </Box>
            </>
          );
        }}
      />
      <Divider />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Input name='purchaseOrderNumber' label='PO Number' />
        </Grid>
        <Grid item xs={6}>
          <Input
            name='projectJobNumber'
            label={requiredLabel('Project Job Number', requiredFields.projectJobNumber)}
            tip={
              submitType === 'saveAsNewQuote'
                ? 'Pre-filled with the project’s job number by default.'
                : undefined
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};
